import React, { useEffect, useRef, useState } from "react";
import PracticeLayout from "../../../components/PracticeLayout";
import PracticeSection from "../../../components/PracticeSection";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

const ROW_HEIGHT = 32;
const TOTAL_ROWS = 10000;
const VISIBLE_ROWS = 12;

export default function LevelAdvanced() {
  useDocumentTitle("Level 7 · Advanced Automation — Practice");
  const [scrollTop, setScrollTop] = useState(0);
  const [authMode, setAuthMode] = useState("cookie"); // "cookie" | "token"
  const [creds, setCreds] = useState({ user: "demo", pass: "demo123" });
  const [authed, setAuthed] = useState(false);
  const [authMsg, setAuthMsg] = useState("");
  const shadowHostRef = useRef();

  // Populate the shadow DOM demo once
  useEffect(() => {
    if (!shadowHostRef.current) return;
    if (shadowHostRef.current.shadowRoot) return;
    const shadow = shadowHostRef.current.attachShadow({ mode: "open" });
    shadow.innerHTML = `
      <style>
        :host { display: block; padding: 12px; border-radius: 6px; background: #f8fafc; }
        .msg { color: #002FA7; font-family: 'IBM Plex Mono', monospace; font-size: 13px; }
        button { margin-top: 8px; padding: 6px 12px; border-radius: 6px; border: 1px solid #d4d4d8; background: white; cursor: pointer; }
      </style>
      <div class="msg">🌒 I live inside a shadow root — try to find me!</div>
      <button data-testid="shadow-btn">Click me (inside shadow)</button>
    `;
    shadow.querySelector("button").addEventListener("click", () => {
      const el = shadow.querySelector(".msg");
      el.textContent = "You reached me through the shadow DOM ✓";
    });
  }, []);

  // Register a Custom Element once
  useEffect(() => {
    if (customElements.get("qa-badge")) return;
    class QaBadge extends HTMLElement {
      connectedCallback() {
        this.textContent = "Custom element — <qa-badge>";
        this.style.cssText = "display:inline-block;padding:6px 12px;border-radius:999px;background:#eef2ff;color:#002FA7;font:600 12px 'IBM Plex Mono',monospace;";
      }
    }
    customElements.define("qa-badge", QaBadge);
  }, []);

  const doAuth = (e) => {
    e.preventDefault();
    if (creds.user === "demo" && creds.pass === "demo123") {
      if (authMode === "cookie") {
        document.cookie = `qat_practice_auth=ok; path=/practice; max-age=600`;
        setAuthMsg("Cookie 'qat_practice_auth=ok' set — assert via document.cookie.");
      } else {
        localStorage.setItem("qat_practice_token", `tok_${Date.now().toString(36)}`);
        setAuthMsg("Token stored in localStorage — assert via localStorage.getItem.");
      }
      setAuthed(true);
    } else {
      setAuthMsg("❌ Invalid credentials — try demo / demo123");
      setAuthed(false);
    }
  };
  const doLogout = () => {
    document.cookie = "qat_practice_auth=; path=/practice; max-age=0";
    localStorage.removeItem("qat_practice_token");
    setAuthed(false);
    setAuthMsg("Logged out.");
  };

  const startIndex = Math.floor(scrollTop / ROW_HEIGHT);
  const endIndex = Math.min(TOTAL_ROWS, startIndex + VISIBLE_ROWS + 3);

  return (
    <PracticeLayout
      eyebrow="Level 7 · Advanced Automation"
      title="The tricky bits"
      subtitle="Iframes, Shadow DOM, virtualised lists, custom elements, and dual auth strategies."
      seo={{
        title: "Level 7 · Advanced Automation — Iframe, Shadow DOM, Auth",
        description: "Practise iframes, Shadow DOM, virtualised 10k-row tables, custom web components and cookie-vs-token authentication.",
        keywords: "shadow dom automation practice, iframe automation, virtualized table testing, custom element automation",
        image: "/og/og-advanced.svg",
      }}
    >
      <PracticeSection id="iframe" title="Iframe" hint="Playwright: frameLocator('#practice-iframe')">
        <iframe
          id="practice-iframe"
          data-testid="practice-iframe"
          title="practice-iframe"
          srcDoc={`<!doctype html><html><body style="font-family: 'IBM Plex Mono', monospace; padding: 16px; background: #fef3c7; color: #92400e;">
            <h3 style="margin:0 0 8px 0;">I'm inside an iframe</h3>
            <p style="font-size:13px;margin:0 0 12px 0;">Switch driver context to interact with me.</p>
            <button data-testid="iframe-btn" onclick="this.textContent='Clicked inside iframe ✓'">Click me inside iframe</button>
            <p style="font-size:12px;margin-top:12px;color:#78350f;">
              <input data-testid="iframe-input" placeholder="Type in iframe" style="padding:6px;border:1px solid #d97706;border-radius:4px;">
            </p>
          </body></html>`}
          className="w-full h-56 border border-zinc-200 rounded"
          sandbox="allow-scripts allow-same-origin"
        />
      </PracticeSection>

      <PracticeSection id="shadow-dom" title="Shadow DOM" hint="Playwright pierces automatically; Selenium needs shadowRoot access.">
        <div ref={shadowHostRef} data-testid="shadow-host" />
      </PracticeSection>

      <PracticeSection id="virtualized-table" title="Virtualized table" hint="Only visible rows exist in the DOM — scroll to load more.">
        <div
          data-testid="virt-container"
          onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
          className="h-[300px] overflow-y-auto border border-zinc-200 rounded bg-white"
        >
          <div style={{ height: TOTAL_ROWS * ROW_HEIGHT, position: "relative" }}>
            {Array.from({ length: endIndex - startIndex }, (_, i) => {
              const idx = startIndex + i;
              return (
                <div
                  key={idx}
                  data-testid={`virt-row-${idx}`}
                  style={{ position: "absolute", top: idx * ROW_HEIGHT, left: 0, right: 0, height: ROW_HEIGHT }}
                  className="px-3 flex items-center border-b border-zinc-100 text-sm tag-mono"
                >
                  Row {idx + 1} — item id {String(idx + 1).padStart(6, "0")}
                </div>
              );
            })}
          </div>
        </div>
        <div className="tag-mono text-[11px] text-zinc-500 mt-1" data-testid="virt-info">
          rendered rows: {endIndex - startIndex} of {TOTAL_ROWS} · scrollTop={scrollTop}
        </div>
      </PracticeSection>

      <PracticeSection id="custom-element" title="Custom web component" hint="Custom elements are opaque to some frameworks.">
        <qa-badge data-testid="custom-element" />
      </PracticeSection>

      <PracticeSection id="auth" title="Authentication (cookie vs token)" hint="demo / demo123">
        <form onSubmit={doAuth} className="grid sm:grid-cols-2 gap-3 max-w-md">
          <div>
            <Label>Username</Label>
            <Input value={creds.user} onChange={(e) => setCreds((c) => ({ ...c, user: e.target.value }))} data-testid="auth-user" className="mt-1.5" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={creds.pass} onChange={(e) => setCreds((c) => ({ ...c, pass: e.target.value }))} data-testid="auth-pass" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Auth mode</Label>
            <div className="flex gap-2 mt-1.5">
              {["cookie", "token"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setAuthMode(m)}
                  data-testid={`auth-mode-${m}`}
                  className={`px-3 py-1.5 rounded text-xs tag-mono border ${
                    authMode === m ? "border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-zinc-200 text-zinc-700 hover:border-zinc-400"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <Button type="submit" data-testid="auth-submit" className="bg-[#002FA7] hover:bg-[#00227A] text-white">Log in</Button>
            {authed && (
              <Button type="button" onClick={doLogout} data-testid="auth-logout" variant="outline">Log out</Button>
            )}
          </div>
          {authMsg && (
            <div data-testid="auth-status" className={`sm:col-span-2 text-sm tag-mono ${authed ? "text-emerald-700" : "text-red-600"}`}>
              {authMsg}
            </div>
          )}
        </form>
      </PracticeSection>
    </PracticeLayout>
  );
}
